import React from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { PlusCircle, TrashFill } from 'react-bootstrap-icons';
import {
  PRODUCT_CURRENCY_OPTIONS,
  PROJECT_PRODUCT_STATUS_OPTIONS,
} from '../../config_and_helpers/config';
import { emptyAdHocFormRow } from '../../utils/projectAdHocPayload';
import DropdownSelect from '../Generic/DropdownSelect';
import RequiredAsteriskComponent from '../Generic/RequiredAsteriskComponent';

/**
 * Editable list of custom (non-catalog) line rows — shared by project form and standalone quote page.
 *
 * @param {Object} props
 * @param {Array<Object>} props.lines
 * @param {function(Array<Object>): void} props.onLinesChange
 * @param {Array<{value:*,label:string}>} props.supplierSelectList
 * @param {boolean} [props.isLoadingSuppliers]
 * @param {boolean} [props.showStatus] - Show status dropdown (project lines only)
 * @param {boolean} [props.readOnly]
 * @param {number|string} [props.maxHeight]
 * @param {string} [props.sectionTitle]
 * @param {string} [props.sectionDescription]
 */
const AdHocLinesEditor = ({
  lines,
  onLinesChange,
  supplierSelectList = [],
  isLoadingSuppliers = false,
  showStatus = true,
  readOnly = false,
  maxHeight = 400,
  sectionTitle = 'Items not in inventory',
  sectionDescription =
    'Record name, CAT#, supplier, and estimated price for items not yet in the inventory catalogue.',
}) => {
  const addLine = () => {
    onLinesChange([...lines, emptyAdHocFormRow()]);
  };

  const removeLine = index => {
    onLinesChange(lines.filter((_, i) => i !== index));
  };

  const updateLine = (index, field, value) => {
    onLinesChange(
      lines.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const setSupplierFromDropdown = (index, option) => {
    onLinesChange(
      lines.map((row, i) => {
        if (i !== index) return row;
        const selectedSupplierId = option?.value || '';
        return {
          ...row,
          line_supplier_id: selectedSupplierId,
          line_supplier_name: selectedSupplierId ? '' : row.line_supplier_name,
        };
      })
    );
  };

  return (
    <Form.Group className="mb-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="mb-0">{sectionTitle}</Form.Label>
        {!readOnly ? (
          <Button variant="outline-secondary" size="sm" onClick={addLine} type="button">
            <PlusCircle className="me-1" />
            Add custom line
          </Button>
        ) : null}
      </div>
      {sectionDescription ? (
        <p className="text-muted small">{sectionDescription}</p>
      ) : null}
      {lines.length === 0 ? (
        <div className="text-muted text-center py-2 border rounded">
          No custom lines. Click &quot;Add custom line&quot; to add one.
        </div>
      ) : (
        <div
          className="border rounded p-3"
          style={{ maxHeight, overflowY: 'auto' }}
        >
          {lines.map((row, index) => (
            <div key={index} className="border rounded p-3 mb-3 bg-white">
              <Row className="g-2">
                <Col xs={12} md={6}>
                  <Form.Label>
                    Name <RequiredAsteriskComponent />
                  </Form.Label>
                  <Form.Control
                    value={row.line_name}
                    onChange={e => updateLine(index, 'line_name', e.target.value)}
                    placeholder="Product name"
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label>CAT#</Form.Label>
                  <Form.Control
                    value={row.line_cat_num}
                    onChange={e => updateLine(index, 'line_cat_num', e.target.value)}
                    placeholder="Catalogue number"
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label>Supplier (from list)</Form.Label>
                  <DropdownSelect
                    optionsList={supplierSelectList}
                    label="Supplier"
                    selectedValue={
                      supplierSelectList.find(s => s.value === row.line_supplier_id) ||
                      null
                    }
                    setSelectedValue={option => setSupplierFromDropdown(index, option)}
                    placeholder="Optional"
                    disabled={readOnly || isLoadingSuppliers}
                    clearable
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label>Supplier name (free text)</Form.Label>
                  <Form.Control
                    value={row.line_supplier_name}
                    onChange={e =>
                      updateLine(index, 'line_supplier_name', e.target.value)
                    }
                    placeholder="When not in supplier list"
                    disabled={readOnly || Boolean(row.line_supplier_id)}
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Label>Price</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={row.line_price}
                    onChange={e => updateLine(index, 'line_price', e.target.value)}
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Label>Currency</Form.Label>
                  <DropdownSelect
                    optionsList={PRODUCT_CURRENCY_OPTIONS}
                    label="Currency"
                    selectedValue={
                      PRODUCT_CURRENCY_OPTIONS.find(
                        c => c.value === row.line_currency
                      ) || null
                    }
                    setSelectedValue={option =>
                      updateLine(index, 'line_currency', option?.value || '')
                    }
                    placeholder="Optional"
                    clearable
                    disabled={readOnly}
                  />
                </Col>
                <Col xs={6} md={showStatus ? 2 : 4}>
                  <Form.Label>Qty</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={row.quantity_needed}
                    onChange={e =>
                      updateLine(index, 'quantity_needed', e.target.value)
                    }
                    disabled={readOnly}
                  />
                </Col>
                {showStatus ? (
                  <Col xs={12} md={4}>
                    <Form.Label>Status</Form.Label>
                    <DropdownSelect
                      optionsList={PROJECT_PRODUCT_STATUS_OPTIONS}
                      label="Status"
                      selectedValue={
                        PROJECT_PRODUCT_STATUS_OPTIONS.find(
                          s => s.value === row.status
                        ) || null
                      }
                      setSelectedValue={option =>
                        updateLine(index, 'status', option?.value || 'NEEDS_QUOTE')
                      }
                      placeholder="Status"
                      disabled={readOnly}
                    />
                  </Col>
                ) : null}
                <Col xs={12}>
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    value={row.notes}
                    onChange={e => updateLine(index, 'notes', e.target.value)}
                    placeholder="Optional notes"
                    disabled={readOnly}
                  />
                </Col>
                {!readOnly ? (
                  <Col xs={12} className="text-end">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      type="button"
                      onClick={() => removeLine(index)}
                    >
                      <TrashFill /> Remove line
                    </Button>
                  </Col>
                ) : null}
              </Row>
            </div>
          ))}
        </div>
      )}
    </Form.Group>
  );
};

export default AdHocLinesEditor;
